// src/modules/academy/components/MyEvents.tsx

import React from 'react';
import Card from '../../../shared/components/ui/Card';
import { UserRole } from '../types';
import { UserParticipation } from '../../../types/user';
import './MyEvents.css';

interface MyEventsProps {
  userRoles: UserRole[];
  participations?: UserParticipation[];
}

const MyEvents: React.FC<MyEventsProps> = ({ userRoles, participations }) => {
  // Función para obtener el estado visual del evento
  const getEventStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      'active': { label: 'Activo', className: 'status-active' },
      'confirmed': { label: 'Confirmado', className: 'status-confirmed' },
      'registered': { label: 'Registrado', className: 'status-registered' },
      'attending': { label: 'Asistiendo', className: 'status-attending' },
      'pending': { label: 'Pendiente', className: 'status-pending' },
      'completed': { label: 'Completado', className: 'status-completed' },
      'cancelled': { label: 'Cancelado', className: 'status-cancelled' }
    };
    
    return statusMap[status] || { label: status, className: 'status-default' };
  };

  // Función para obtener el icono del rol
  const getRoleIcon = (role: string) => {
    const roleIcons: Record<string, string> = {
      'participant': '👨‍🎓',
      'speaker': '🎤',
      'judge': '⚖️'
    };
    return roleIcons[role] || '👤';
  };

  // Función para formatear fechas
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Fecha no disponible';
    
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Solo usar participaciones reales
  const events = participations || [];

  // Filtrar eventos por roles del usuario
  const filteredEvents = events.filter(event => 
    userRoles.includes(event.role as UserRole)
  );

  // Si no hay eventos filtrados, mostrar todos los disponibles
  const displayEvents = filteredEvents.length > 0 ? filteredEvents : events;

  return (
    <div className="my-events">
      <div className="events-header">
        <h2 className="events-title">Mis Eventos</h2>
        <p className="events-subtitle">
          {events.length > 0 
            ? `${events.length} participaciones registradas en la base de datos`
            : 'No hay participaciones registradas en la base de datos'
          }
        </p>
      </div>

      {displayEvents.length === 0 ? (
        <Card className="no-events" padding="large">
          <div className="no-events-content">
            <div className="no-events-icon">📅</div>
            <h3>No tienes eventos registrados</h3>
            <p>
              Cuando te registres o participes en eventos, aparecerán aquí.
            </p>
            <div className="no-events-actions">
              <button className="explore-btn" disabled>
                Explorar Eventos Disponibles
              </button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="events-list">
          {displayEvents.map((event) => {
            const statusBadge = getEventStatusBadge(event.status);
            
            return (
              <Card 
                key={`event-${event.id}`}
                className={`event-card event-card-${event.id}`}
                variant="glass"
                padding="large"
              >
                <div className="event-content">
                  <div className="event-header">
                    <div className="event-title-section">
                      <h3 className="event-title">
                        {event.event_title || `Evento #${event.event_id}`}
                      </h3>
                      {event.feedback && (
                        <p className="event-description">{event.feedback}</p>
                      )}
                    </div>
                    
                    <div className="event-badges">
                      <span className={`status-badge ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                      <span className="role-badge">
                        {getRoleIcon(event.role)} {event.role}
                      </span>
                    </div>
                  </div>

                  <div className="event-details">
                    {(event.start_date || event.end_date) && (
                      <div className="event-dates">
                        {event.start_date && (
                          <div className="date-item">
                            <span className="date-label">Inicio:</span>
                            <span className="date-value">{formatDate(event.start_date)}</span>
                          </div>
                        )}
                        {event.end_date && (
                          <div className="date-item">
                            <span className="date-label">Fin:</span>
                            <span className="date-value">{formatDate(event.end_date)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="event-meta">
                      <div className="meta-item">
                        <span className="meta-label">Registrado:</span>
                        <span className="meta-value">{formatDate(event.registration_date)}</span>
                      </div>
                      {event.payment_status && (
                        <div className="meta-item">
                          <span className="meta-label">Pago:</span>
                          <span className="meta-value">{event.payment_status}</span>
                        </div>
                      )}
                      {event.event_id && (
                        <div className="meta-item">
                          <span className="meta-label">ID Evento:</span>
                          <span className="meta-value">#{event.event_id}</span>
                        </div>
                      )}
                    </div>

                    {event.certificate_url && (
                      <div className="event-certificate">
                        <span className="certificate-icon">🏆</span>
                        <span>Certificado disponible</span>
                      </div>
                    )}

                    {event.final_score !== undefined && (
                      <div className="event-score">
                        <span className="score-label">Puntuación:</span>
                        <span className="score-value">{event.final_score}</span>
                      </div>
                    )}
                  </div>

                  <div className="event-actions">
                    <button className="action-btn primary" disabled>
                      Ver Detalles
                    </button>
                    <button className="action-btn secondary" disabled>
                      {event.role === 'participant' ? 'Continuar Curso' : 'Gestionar'}
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {displayEvents.length > 5 && (
        <Card className="view-more" padding="medium">
          <button className="view-more-btn" disabled>
            Ver todos los eventos ({displayEvents.length})
          </button>
        </Card>
      )}

      <div className="events-info">
        <Card variant="flat" padding="medium">
          <p>
            📊 Mostrando datos reales de participaciones desde la base de datos. 
            {events.length === 0 && ' Regístrate en eventos para ver tu actividad aquí.'}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default MyEvents;